// src/components/PageHeader.js

import React, { useState, useEffect } from 'react';
import { Plus, Search, X, HelpCircle, Info, ShieldAlert, LoaderCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { AnimatePresence, motion } from "framer-motion";
import { useMediaQuery } from "../hooks/use-media-query";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogClose
} from "../components/ui/dialog";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator
} from "../components/ui/dropdown-menu";
import { getConflictTypeLabel, getConfidenceDisplay, getPriorityDisplay } from '../utils/conflictDetection';

const PageHeader = ({
  title,
  description,
  icon: Icon,
  tooltip,
  searchQuery = '',
  onSearchChange,
  onAddClick,
  filterButton = null,
  className = '',
  children,
  tabs = [],
  activeTab = '',
  onTabChange,
  contentSection = null,
  isOnboarding = false,
  helpContent = null,
  helpTitle = 'Help & Documentation',
  addButtonClassName = "",
  buttons = [],
  conflicts = [],
  isLoadingConflicts = false,
  isProcessingConflicts = false,
  showConflicts = false
}) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [currentConflictIndex, setCurrentConflictIndex] = useState(0);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 640px)");
  
  // Function to handle opening the help panel
  const handleOpenHelp = () => {
    // First, close the dialog
    setIsHelpOpen(false);
    
    // Then, call the helpContent function if it exists
    if (typeof helpContent === 'function') {
      setTimeout(() => {
        helpContent();
      }, 100); // Short delay to ensure dialog is closed
    }
  };
  
  useEffect(() => {
    // Animate header in on mount
    setIsHeaderVisible(true);
    
    // Set document title to match the header title
    // This ensures consistent titles during lazy loading
    if (title) {
      document.title = title;
    }
  }, [title]);

  // Determine if we have a simple layout case (no search + only one button) for mobile
  const hasSearch = !!onSearchChange;
  const conflictButtonCount = showConflicts ? 1 : 0; // Use showConflicts prop instead of conflicts !== undefined
  const helpButtonCount = helpContent ? 1 : 0;
  const buttonCount = (filterButton ? 1 : 0) + (onAddClick ? 1 : 0) + (React.Children.count(children) > 0 ? 1 : 0) + (buttons.length || 0) + conflictButtonCount + helpButtonCount;
  const isSimpleMobileLayout = isMobile && !hasSearch && buttonCount === 1;

  // Calculate the height for the spacer element when header is fixed on mobile - more precise calculation
  const calculateHeaderHeight = () => {
    // Base height for title row (now includes add/conflict buttons on mobile)
    let baseHeight = isMobile ? 75 : 70; // Slightly taller on mobile for the buttons
    
    // Add height for description if present
    if (description) {
      baseHeight += isMobile ? 40 : 30;
      
      // Add extra spacing only if tabs are also present
      if (tabs.length > 0) {
        baseHeight += 10; // Additional spacing between description and tabs
      }
    }
    
    // Add height for tabs section if present
    if (tabs.length > 0) {
      baseHeight += 50;
    }
    
    // Add height for search row on mobile (if search is enabled)
    if (isMobile && onSearchChange) {
      baseHeight += 60;
    }
    
    // Add height for search/buttons row on desktop if not in simple layout
    if (!isMobile && !isSimpleMobileLayout && (hasSearch || buttonCount > 0)) {
      baseHeight += 60;
    }
    
    // Add some buffer for different screen sizes and text lengths
    const buffer = 5; // Reduced buffer since spacing is now more precise
    
    return `${baseHeight + buffer}px`;
  };

  // Mobile menu toggle removed - now handled by TopNav

  // Navigate to next conflict
  const nextConflict = () => {
    setCurrentConflictIndex((prev) => 
      prev < conflicts.length - 1 ? prev + 1 : 0
    );
  };

  // Navigate to previous conflict
  const prevConflict = () => {
    setCurrentConflictIndex((prev) => 
      prev > 0 ? prev - 1 : conflicts.length - 1
    );
  };

  // Reset conflict index when conflicts change
  useEffect(() => {
    setCurrentConflictIndex(0);
  }, [conflicts.length]);

  // Conflict notification component - Only show when conflicts are provided and managed
  const ConflictNotification = conflicts !== undefined && showConflicts && (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
      whileTap={{ 
        scale: 0.98,
        transition: { duration: 0.1, ease: "easeIn" }
      }}
      className="flex-shrink-0"
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 15,
        delay: 0.25
      }}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="icon" 
            className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center p-0 shadow-sm border-gray-200 relative hover:shadow-md transition-all duration-200"
            aria-label={conflicts.length > 0 ? `${conflicts.length} conflict${conflicts.length !== 1 ? 's' : ''} detected` : 'No conflicts detected'}
          >
            <motion.div
              animate={isProcessingConflicts ? {} : {}}
              transition={isProcessingConflicts ? {} : {}}
            >
              <ShieldAlert className="h-4 w-4 text-gray-700" />
            </motion.div>
            
            {/* Red notification badge - show conflict count or mysterious indicator when processing */}
            {(conflicts.length > 0 || isProcessingConflicts) && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm"
              >
                {isProcessingConflicts ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <LoaderCircle className="h-3 w-3" />
                  </motion.div>
                ) : (
                  conflicts.length
                )}
              </motion.span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className={`${isMobile ? 'w-[350px] max-w-[95vw]' : 'w-[400px] max-w-[90vw]'} p-0 overflow-hidden shadow-xl border-0 bg-white/95 backdrop-blur-sm z-[10000]`}
          sideOffset={8}
        >
          {conflicts.length > 0 ? (
            <>
              {/* Header */}
              <div className={`${isMobile ? 'px-4 py-4' : 'px-6 py-5'} border-b border-gray-100/80 bg-gradient-to-r from-white to-gray-50/50`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`font-semibold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>Mixed Messages</h3>
                    <p className={`text-gray-400 font-light ${isMobile ? 'text-[10px]' : 'text-xs'}`}>Fix these so customers get consistent answers</p>
                  </div>
                </div>
              </div>

              {/* Conflict Card Container */}
              <div className={`relative ${isMobile ? 'h-80' : 'h-96'} overflow-hidden`}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentConflictIndex}
                    initial={{ opacity: 0, x: 30, scale: 0.98 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -30, scale: 0.98 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 300, 
                      damping: 25,
                      duration: 0.4
                    }}
                    className={`absolute inset-0 ${isMobile ? 'p-4' : 'p-6'} ${conflicts.length > 1 ? 'pb-16' : ''}`}
                  >
                    {(() => {
                      const conflict = conflicts[currentConflictIndex];
                      return (
                        <div className="h-full flex flex-col">
                          {/* Conflict Type Badge */}
                          <div className="flex items-center gap-2 mb-4">
                            <motion.span 
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.1 }}
                              className={`font-semibold px-3 py-1.5 rounded-full bg-red-100 text-red-700 border border-red-200 ${isMobile ? 'text-xs' : 'text-xs'}`}
                            >
                              {getConflictTypeLabel(conflict.conflict_type)}
                            </motion.span>
                            
                            {/* View Affected Settings Button */}
                            <DropdownMenu open={isSettingsDropdownOpen} onOpenChange={setIsSettingsDropdownOpen}>
                              <DropdownMenuTrigger asChild>
                                <button 
                                  className={`${isMobile ? 'text-xs px-2 py-1' : 'text-xs px-3 py-1.5'} bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors cursor-pointer border-0 focus:outline-none focus:ring-2 focus:ring-gray-300`}
                                  aria-label={`View ${conflicts[currentConflictIndex]?.settings?.length || 0} affected settings`}
                                >
                                  View Affected Settings
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent 
                                align="end" 
                                className={`${isMobile ? 'w-64 max-w-[85vw]' : 'w-72 max-w-[90vw]'} p-0 overflow-hidden shadow-lg border border-gray-200 z-[10001]`}
                                sideOffset={5}
                              >
                                <div className={`${isMobile ? 'px-3 py-2' : 'px-4 py-3'} border-b border-gray-100 bg-gray-50`}>
                                  <h4 className={`font-medium text-gray-900 ${isMobile ? 'text-xs' : 'text-sm'}`}>Affected Settings</h4>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {conflicts[currentConflictIndex]?.settings?.length || 0} settings involved in this conflict
                                  </p>
                                </div>
                                <div className="max-h-64 overflow-y-auto">
                                  {conflicts[currentConflictIndex]?.settings?.map((setting, index) => (
                                    <div 
                                      key={setting.id} 
                                      className={`${isMobile ? 'px-3 py-2' : 'px-4 py-3'} hover:bg-gray-50 transition-colors ${
                                        index < (conflicts[currentConflictIndex]?.settings?.length - 1) ? 'border-b border-gray-100' : ''
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                          <p className={`font-medium text-gray-900 truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                            {setting.name}
                                          </p>
                                          <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                              {setting.category_name}
                                            </span>
                                            <span className="text-xs text-gray-400 capitalize">
                                              {setting.type?.replace('_', ' ')}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )) || (
                                    <div className={`${isMobile ? 'px-3 py-4' : 'px-4 py-6'} text-center text-gray-500`}>
                                      <p className={`${isMobile ? 'text-xs' : 'text-sm'}`}>No settings information available</p>
                                    </div>
                                  )}
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {/* Conflict Reason & Suggestion */}
                          <div className="flex-1 min-h-0 flex flex-col">
                            <motion.h4 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2 }}
                              className={`font-semibold text-gray-900 mb-3 leading-relaxed tracking-tight ${isMobile ? 'text-xs' : 'text-sm'}`}
                            >
                              {conflict.reason}
                            </motion.h4>
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.25 }}
                              className={`text-gray-600 leading-relaxed overflow-y-auto flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 ${isMobile ? 'text-xs' : 'text-xs'}`}
                            >
                              {conflict.suggestion}
                            </motion.div>
                          </div>
                        </div>
                      );
                    })()}
                  </motion.div>
                </AnimatePresence>
                
                {/* Navigation Controls - Fixed outside AnimatePresence */}
                {conflicts.length > 1 && (
                  <div className={`absolute bottom-4 left-4 right-4 pt-4 border-t border-gray-100/80 bg-white/95`}>
                    <div className="flex items-center justify-between">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={prevConflict}
                        className="p-2 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm border border-gray-200"
                        aria-label="Previous conflict"
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </motion.button>

                      {/* Pagination dots */}
                      <div className="flex justify-center gap-2">
                        {conflicts.map((_, index) => (
                          <motion.button
                            key={index}
                            onClick={() => setCurrentConflictIndex(index)}
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                            className={`h-2 rounded-full transition-all duration-300 ${
                              index === currentConflictIndex 
                                ? 'bg-red-500 w-6 shadow-sm' 
                                : 'bg-gray-300 w-2 hover:bg-gray-400'
                            }`}
                            aria-label={`Go to conflict ${index + 1}`}
                          />
                        ))}
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={nextConflict}
                        className="p-2 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm border border-gray-200"
                        aria-label="Next conflict"
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </motion.button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={`${isMobile ? 'px-4 py-8' : 'px-6 py-10'} text-center`}>
              <motion.div 
                initial={{ scale: 0.8, opacity: 0, y: 10 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25, delay: 0.1 }}
                className={`mx-auto ${isMobile ? 'w-12 h-12' : 'w-16 h-16'} rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center text-green-600 mb-4 shadow-sm border border-green-100/50`}
              >
                <svg className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </motion.div>
              <motion.h4 
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`font-semibold text-gray-900 mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}
              >
                All Clear!
              </motion.h4>
              <motion.p 
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className={`text-gray-500 font-light leading-relaxed max-w-xs mx-auto ${isMobile ? 'text-xs' : 'text-xs'}`}
              >
                Your brain settings are consistent and conflict-free
              </motion.p>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </motion.div>
  );

  // Help button component - Only show if helpContent is provided
  const HelpButton = helpContent && (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
      whileTap={{ 
        scale: 0.98,
        transition: { duration: 0.1, ease: "easeIn" }
      }}
      className="flex-shrink-0"
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 15,
        delay: 0.3
      }}
    >
      <Button 
        variant="outline" 
        size="icon" 
        className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center p-0 shadow-sm border-gray-200"
        onClick={handleOpenHelp}
        aria-label="Help & Documentation"
      >
        <HelpCircle className="h-4 w-4 text-gray-700" />
      </Button>
    </motion.div>
  );

  // Determine which single button to show (if we're in simple layout)
  const singleButton = ConflictNotification || HelpButton; // Prioritize conflict notification if available, fallback to help

  return (
    <>
      {/* Spacer element to prevent content from being hidden under fixed header on mobile */}
      {isMobile && <div style={{ height: calculateHeaderHeight() }} />}
      
      <motion.div 
        className={`
          ${isMobile 
            ? 'fixed top-0 left-0 right-0 z-[9990] bg-white shadow-md' 
            : tabs.length === 0 
              ? 'border-b border-gray-100' 
              : ''}
          ${className}
        `}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: isHeaderVisible ? 1 : 0, y: isHeaderVisible ? 0 : -10 }}
        transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
      >
        <div className={`
          ${isMobile ? 'py-3 px-4' : 'container mx-auto py-5 sm:py-6 px-4 sm:px-6 max-w-7xl'}
        `}>
          {/* Title and Actions Row */}
          <div className={`flex ${(isSimpleMobileLayout || (isMobile && (showConflicts || onAddClick))) ? 'flex-row items-center justify-between' : 'flex-col sm:flex-row sm:items-center justify-between'} ${
            description || tabs.length > 0 ? 'mb-3 sm:mb-4' : 'mb-0'
          } gap-3 sm:gap-6`}>
            {/* Left side - Title and optional tooltip */}
            <motion.div 
              className="flex items-center space-x-3 sm:space-x-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              {Icon && (
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  className="flex-shrink-0"
                >
                  <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-gray-700" />
                </motion.div>
              )}
              
              <div className="flex flex-col">
                <h1 className={`${isMobile ? 'text-xl' : 'text-2xl sm:text-3xl'} font-bold tracking-tight truncate`}>{title}</h1>
                
                {/* Description for mobile view (placed directly under title) */}
                {isMobile && description && (
                  <motion.p 
                    className="text-xs sm:text-sm text-gray-500 mt-1"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                  >
                    {description}
                  </motion.p>
                )}
              </div>
              
              {tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <motion.div 
                        whileHover={{ scale: 1.1 }} 
                        whileTap={{ scale: 0.95 }}
                        className="flex-shrink-0"
                      >
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 rounded-full">
                          <HelpCircle className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                        </Button>
                      </motion.div>
                    </TooltipTrigger>
                    <TooltipContent 
                      className="bg-white p-3 sm:p-4 text-gray-700 text-sm max-w-xs rounded-md border border-gray-200 shadow-lg"
                      sideOffset={5}
                      align={isMobile ? "center" : "start"}
                    >
                      <p className="leading-relaxed">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </motion.div>
            
            {/* Mobile: Add button and conflict alerts on the same line as hamburger menu */}
            {isMobile && (
              <motion.div 
                className="flex items-center space-x-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                {/* Conflict Notification */}
                {ConflictNotification}
                
                {/* Add button */}
                {onAddClick && (
                  <button
                    onClick={onAddClick}
                    className={`
                      flex items-center justify-center h-9 w-9 rounded-lg 
                      border border-gray-200 bg-white shadow-sm transition-all duration-300 ease-in-out
                      hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200 
                      focus:ring-offset-1 ${addButtonClassName}
                    `}
                    aria-label="Add New"
                  >
                    <Plus className={`h-5 w-5 ${addButtonClassName.includes('text-blue-700') ? 'text-blue-700' : 'text-gray-500'}`} />
                  </button>
                )}
              </motion.div>
            )}
            
            {/* Show the single button inline for simple mobile layouts */}
            {isSimpleMobileLayout && singleButton}
            
            {/* Right side - Search, filter, custom elements, and Add button - Only show for desktop or complex mobile layouts */}
            {(!isSimpleMobileLayout && !isMobile) && (
              <motion.div 
                className="flex w-full sm:w-auto items-center space-x-2 sm:space-x-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                {onSearchChange && (
                  <motion.div 
                    className={`relative flex-1 w-full sm:w-[300px] md:w-[350px] transition-all duration-300 ease-in-out ${isSearchFocused ? 'sm:w-[340px] md:w-[390px]' : ''}`}
                    layout
                  >
                    <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isSearchFocused ? 'text-gray-700' : 'text-gray-400'} h-4 w-4 transition-colors duration-300`} />
                    <Input 
                      placeholder="Search..." 
                      value={searchQuery}
                      onChange={(e) => onSearchChange(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      className={`pl-10 h-9 sm:h-10 border-gray-200 shadow-sm ${isSearchFocused ? 'ring-2 ring-gray-200 ring-opacity-50' : ''} transition-all duration-300`}
                    />
                    <AnimatePresence>
                      {searchQuery && (
                        <motion.button
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.15 }}
                          onClick={() => onSearchChange('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2"
                        >
                          <X className="h-4 w-4" />
                        </motion.button>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
                
                {/* Filter button on mobile */}
                {filterButton && (
                  <motion.div
                    className="flex-shrink-0"
                    whileHover={{ 
                      scale: 1.02,
                      transition: { duration: 0.2, ease: "easeOut" }
                    }}
                    whileTap={{ 
                      scale: 0.98,
                      transition: { duration: 0.1, ease: "easeIn" }
                    }}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 300, 
                      damping: 15,
                      delay: 0.35
                    }}
                  >
                    {filterButton}
                  </motion.div>
                )}
                
                {!isSimpleMobileLayout && children}
                
                {/* Custom Buttons Array - NEW: Render custom buttons */}
                {!isSimpleMobileLayout && buttons.length > 0 && buttons.map((button, index) => {
                  const ButtonIcon = button.icon;
                  return (
                    <motion.div
                      key={index}
                      whileHover={{ 
                        scale: 1.02,
                        transition: { duration: 0.2, ease: "easeOut" }
                      }}
                      whileTap={{ 
                        scale: 0.98,
                        transition: { duration: 0.1, ease: "easeIn" }
                      }}
                      className="flex-shrink-0"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 300, 
                        damping: 15,
                        delay: 0.3 + (index * 0.05)
                      }}
                    >
                      <Button 
                        variant={button.variant === 'primary' ? 'default' : 'outline'}
                        size="sm"
                        className={`h-9 sm:h-10 flex items-center justify-center gap-2 shadow-sm border-gray-200 ${button.className || ''} ${
                          button.disabled ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={button.onClick}
                        disabled={button.disabled}
                        aria-label={button.label}
                      >
                        {ButtonIcon && <ButtonIcon className="h-4 w-4" />}
                        <span className="text-xs sm:text-sm">{button.label}</span>
                      </Button>
                    </motion.div>
                  );
                })}
                
                {/* Conflict Notification - Show before help and add buttons */}
                {ConflictNotification}
                
                {/* Help Button - Add before the "Add" button */}
                {helpContent && HelpButton}
                
                {onAddClick && !isSimpleMobileLayout && (
                  <button
                    onClick={onAddClick}
                    className={`
                      flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg 
                      border border-gray-200 bg-white shadow-sm transition-all duration-300 ease-in-out
                      hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200 
                      focus:ring-offset-1 ${addButtonClassName}
                    `}
                    aria-label="Add New"
                  >
                    <Plus className={`h-5 w-5 ${addButtonClassName.includes('text-blue-700') ? 'text-blue-700' : 'text-gray-500'}`} />
                  </button>
                )}
              </motion.div>
            )}
          </div>
          
          {/* Description (if provided) - Only show on desktop */}
          {!isMobile && (
            <AnimatePresence>
              {description && (
                <motion.p 
                  className={`text-sm text-gray-500 max-w-3xl ${
                    tabs.length > 0 ? 'mb-4 sm:mb-5' : 'mb-0'
                  }`}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  {description}
                </motion.p>
              )}
            </AnimatePresence>
          )}
          
          {/* Tabs Section (if provided) */}
          {tabs.length > 0 && onTabChange && (
            <motion.div 
              className={`
                ${isMobile ? 'border-b border-gray-200 mb-2 mt-2 px-0' : 'border-b border-gray-200 mb-0 mt-4 sm:mt-5 -mx-4 sm:mx-0 px-4 sm:px-0'}
              `}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <ScrollArea className="w-full overflow-auto pb-1">
                <div className="flex min-w-max space-x-3 sm:space-x-8">
                  {tabs.map((tab, index) => (
                    <React.Fragment key={tab.value}>
                      {(tab.separator || tab.isConfigTab) && index > 0 && (
                        <div className="h-8 w-px bg-gray-200 self-center mx-1" />
                      )}
                      <motion.button
                        onClick={() => onTabChange(tab.value)}
                        disabled={tab.disabled}
                        className={`
                          relative flex flex-row items-center py-2.5 sm:py-3 px-1 font-medium text-xs sm:text-sm transition-colors
                          ${activeTab === tab.value 
                            ? 'text-gray-900' 
                            : 'text-gray-500 hover:text-gray-700'}
                          ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        whileHover={!tab.disabled ? { y: -1 } : {}}
                        whileTap={!tab.disabled ? { y: 0 } : {}}
                      >
                        {tab.icon && (
                          <motion.div 
                            className="mr-1.5 sm:mr-2 flex-shrink-0"
                            initial={false}
                            animate={{ rotate: activeTab === tab.value ? 0 : 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                          >
                            <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          </motion.div>
                        )}
                        <span>{tab.label}</span>
                        {tab.count > 0 && (
                          <motion.span 
                            className={`ml-1 sm:ml-2 py-0.5 px-1.5 sm:px-2 rounded-full text-xs ${tab.countColor || 'bg-gray-100 text-gray-700'}`}
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 400, damping: 20 }}
                          >
                            {tab.count}
                          </motion.span>
                        )}
                        {activeTab === tab.value && (
                          <motion.div
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"
                            layoutId="activeTabIndicator"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                          />
                        )}
                      </motion.button>
                    </React.Fragment>
                  ))}
                </div>
              </ScrollArea>
            </motion.div>
          )}
          
          {/* Content Section (if provided) */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab || 'content'}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: isMobile ? 0.2 : 0.3, type: "tween" }}
            >
              {contentSection}
            </motion.div>
          </AnimatePresence>
          
          {/* Mobile: Search and other buttons on a separate line */}
          {isMobile && onSearchChange && (
            <motion.div 
              className="flex w-full items-center space-x-2 mb-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <motion.div 
                className={`relative flex-1 transition-all duration-300 ease-in-out ${isSearchFocused ? 'scale-[1.02]' : ''}`}
                layout
              >
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isSearchFocused ? 'text-gray-700' : 'text-gray-400'} h-4 w-4 transition-colors duration-300`} />
                <Input 
                  placeholder="Search..." 
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  className={`pl-10 h-9 sm:h-10 border-gray-200 shadow-sm ${isSearchFocused ? 'ring-2 ring-gray-200 ring-opacity-50' : ''} transition-all duration-300`}
                />
                <AnimatePresence>
                  {searchQuery && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.15 }}
                      onClick={() => onSearchChange('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2"
                    >
                      <X className="h-4 w-4" />
                    </motion.button>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Help Dialog - Now with improved accessibility */}
      <Dialog open={isHelpOpen} onOpenChange={setIsHelpOpen}>
        <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden border-none shadow-xl" aria-describedby="help-dialog-description">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle>{helpTitle}</DialogTitle>
            <DialogDescription id="help-dialog-description" className="sr-only">
              Help and documentation for using this feature
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="p-6 max-h-[70vh]">
            <div className="prose prose-sm">
              <p className="text-center text-gray-500 mb-4">
                For detailed help and documentation, use our comprehensive guide:
              </p>
              
              <div className="flex justify-center mt-6">
                <Button
                  className="w-full"
                  onClick={handleOpenHelp}
                >
                  Open Help Guide
                </Button>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PageHeader;